import { onMounted } from 'vue'
import { useAuth } from '../auth'
import { api, ApiError, EP } from '../api'
import type { SyxUser } from '../types'

/**
 * Sync session data from server on app mount
 * Server is the source of truth - fetch fresh user data
 */
/**
 * Refresh the session's role from the server on app mount.
 *
 * `onInvalidated` runs when the server rejects the stored session, so the
 * host app can route to its login screen. Kept as a callback so this
 * composable stays router-agnostic.
 */
export function useSessionSync(options: { onInvalidated?: () => void } = {}) {
  const auth = useAuth()

  onMounted(async () => {
    if (!auth.isLoggedIn.value || !auth.isSyx.value || !auth.user.value) {
      return
    }

    try {
      // Server is the source of truth for the user's role.
      const userData = await api<SyxUser>(
        EP.ADMIN_SYX_DETAIL,
        { i: auth.user.value.id },
        auth.getToken(),
      )

      auth.updateUser({
        ...auth.user.value,
        role: userData.role,
      })
    } catch (error) {
      // A rejected session (expired, revoked, or banned) leaves localStorage
      // claiming the user is signed in, which would show the admin shell and
      // let the route guard through. Purge it and return to login instead.
      if (error instanceof ApiError && error.code === 'unauthorized') {
        auth.clearSession()
        options.onInvalidated?.()
        return
      }
      // Transient failures (network, server error) must not log the user out.
      console.error('Session sync failed:', error)
    }
  })
}
